import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TablePaginationConfig } from 'antd/es/table';
import qs from 'qs';
import { Dayjs } from 'dayjs';
import { ParsedUrlQuery } from 'querystring';
import { Divider, Row, Space } from 'antd';
import { useLocalStorageState } from 'ahooks';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import ContainerWrapper from 'Common/Components/ContainerWrapper';
import CursorPagination from 'ShorePay/components/CursorPagination';
import { useUsers } from 'Contacts/hooks/users';
import {
  useTipsCSVReport,
  useTipsPerTransaction,
  useTipsSummary,
} from 'Reports/hooks/tip-reports';
import {
  ReportType,
  TipsCSVReportType,
  TipsSummaryReportParams,
  TipPerTransactionReportParams,
  Cursor,
} from 'utils/apiClient/services/reports/types';
import TipsSummaryReportTable from './views/TipsSummaryReportTable';
import TipPerTransactionTable from './views/TipPerTransactionTable';
import TipReportHeader from './views/TipReportHeader';

export type TipsProps = {
  query: ParsedUrlQuery;
  dateFilter: Dayjs[];
  currency: string;
};

const TipsReport = ({ dateFilter, query, currency }: TipsProps) => {
  const [startDate, endDate] = useMemo(() => dateFilter, [dateFilter]);
  const [groupBy, setGroupBy] = useState<ReportType>(ReportType.Daily);
  const users = useUsers({ search: null });
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const usersList = useMemo(() => users.data, [users]);
  const enabledFlag = useMemo(() => query._ === '5', [query]);
  const summaryTableFilters = {
    type: groupBy,
    users: usersList?.map((user) => user.id),
    ...(startDate &&
      endDate && {
        created_at_start: startDate.toISOString(),
        created_at_end: endDate.toISOString(),
      }),
  };

  const listTableFilters = {
    users: usersList?.map((user) => user.id),
    ...(startDate &&
      endDate && {
        created_at_start: startDate.toISOString(),
        created_at_end: endDate.toISOString(),
      }),
  };

  const [summaryTableParams, setSummaryTableParams] =
    useState<TipsSummaryReportParams>({
      filters: summaryTableFilters,
    });

  const [listTableParams, setListTableParams] =
    useState<TipPerTransactionReportParams>({
      filters: listTableFilters,
      cursor: null,
    });

  const memoizedTableParams = useMemo(
    () => ({
      summaryTableParams,
      listTableParams,
    }),
    [summaryTableParams, listTableParams]
  );

  const tipsSummaryQuery = useTipsSummary({
    tableParams: memoizedTableParams.summaryTableParams,
    enabled: enabledFlag,
  });

  const tipsReportsQuery = useTipsPerTransaction({
    tableParams: memoizedTableParams.listTableParams,
    enabled: enabledFlag,
  });

  const createSummaryCSVReport = useTipsCSVReport({
    accountId: account?.id,
    group_by: groupBy,
    user_id: summaryTableParams.filters?.users,
    created_at_start: summaryTableParams?.filters?.created_at_start,
    created_at_end: summaryTableParams?.filters?.created_at_end,
    report_type: TipsCSVReportType.Summary,
  });

  const createListCSVReport = useTipsCSVReport({
    accountId: account?.id,
    user_id: listTableParams.filters?.users,
    created_at_start: listTableParams?.filters?.created_at_start,
    created_at_end: listTableParams?.filters?.created_at_end,
    report_type: TipsCSVReportType.List,
  });

  const updateSummaryTableParams = useCallback(() => {
    setSummaryTableParams((prevTableParams) => ({
      ...prevTableParams,
      filters: {
        ...prevTableParams.filters,
        created_at_start: startDate,
        created_at_end: endDate,
      },
    }));
  }, [startDate, endDate]);

  const updateListTableParams = useCallback(() => {
    setListTableParams((prevTableParams) => ({
      ...prevTableParams,
      filters: {
        ...prevTableParams.filters,
        created_at_start: startDate,
        created_at_end: endDate,
      },
    }));
  }, [startDate, endDate]);

  useEffect(() => {
    updateSummaryTableParams();
    updateListTableParams();
  }, [updateSummaryTableParams, updateListTableParams]);
  const isSummaryDownloadDisabled =
    (tipsSummaryQuery.data && tipsSummaryQuery.data.length === 0) ||
    tipsSummaryQuery.isLoading ||
    tipsSummaryQuery.isError;

  const isListDownloadDisabled =
    (tipsReportsQuery.data && tipsReportsQuery.data.results.length === 0) ||
    tipsReportsQuery.isLoading ||
    tipsReportsQuery.isError;

  const onHandleSummaryDownload = () => {
    createSummaryCSVReport.refetch();
  };

  const onHandleListDownload = () => {
    createListCSVReport.refetch();
  };

  const handleSummaryTableChange = (params: TipsSummaryReportParams) => {
    setSummaryTableParams((state: TipsSummaryReportParams) => ({
      ...state,
      pagination: {
        ...(params.pagination as TablePaginationConfig),
      },
    }));
  };

  const handleListTableChange = (params: Cursor) => {
    if (params !== null && typeof params === 'string') {
      const obj = qs.parse(params.split('?')[1]);
      setListTableParams((state: TipPerTransactionReportParams) => ({
        ...state,
        cursor: obj.cursor,
      }));
    }
  };

  const handleSummaryUsersFilter: (userList: number[]) => void = (userList) =>
    setSummaryTableParams((state: TipsSummaryReportParams) => ({
      ...state,
      filters: {
        ...state.filters,
        users: userList,
      },
    }));

  const handleListUsersFilter = (userList: number[]) =>
    setListTableParams((state: TipPerTransactionReportParams) => ({
      ...state,
      filters: {
        ...state.filters,
        users: userList,
      },
    }));

  const handleReportTypeChange = (type: ReportType) => {
    setGroupBy(type);
    setSummaryTableParams((state: TipsSummaryReportParams) => ({
      ...state,
      filters: {
        ...state.filters,
        type,
      },
    }));
  };

  return (
    <Space direction="vertical" css="width: 100%; overflow:auto">
      <ContainerWrapper
        css={{
          height: '20%',
          minHeight: '0 !important',
          padding: '1em',
        }}
      >
        <TipReportHeader
          reportType="summary"
          users={usersList ?? []}
          rangeReportType={groupBy}
          handleUsersFilterChange={handleSummaryUsersFilter}
          handleDownload={onHandleSummaryDownload}
          isDisabled={isSummaryDownloadDisabled}
          handleReportTypeChange={handleReportTypeChange}
        />
        <Divider css="margin: 0" />
        <TipsSummaryReportTable
          reportData={tipsSummaryQuery.data?.map((data, index) => ({
            ...data,
            key: index,
          }))}
          tableParams={summaryTableParams}
          handleTableChange={handleSummaryTableChange}
          currency={currency}
          isDataLoading={tipsSummaryQuery.isLoading}
          pagination={false}
        />
      </ContainerWrapper>
      <ContainerWrapper
        css={{ minHeight: '0 !important', padding: '1em', marginTop: '10px' }}
      >
        <TipReportHeader
          reportType="list"
          users={usersList ?? []}
          handleDownload={onHandleListDownload}
          isDisabled={isListDownloadDisabled}
          handleUsersFilterChange={handleListUsersFilter}
        />
        <Divider css="margin: 0" />
        <TipPerTransactionTable
          reportData={tipsReportsQuery.data?.results}
          tableParams={listTableParams}
          handleTableChange={handleListTableChange}
          currency={currency}
          isDataLoading={tipsReportsQuery.isLoading}
          pagination={false}
        />
        <Row justify="end" css={{ marginTop: '5px' }}>
          <CursorPagination
            previous={tipsReportsQuery.data?.previous}
            next={tipsReportsQuery.data?.next}
            onChange={(cursor: Cursor) => handleListTableChange(cursor)}
          />
        </Row>
      </ContainerWrapper>
    </Space>
  );
};

export default TipsReport;
